/**
 * Theme names, the brand colour each contributes, and the accents derived from it.
 * No DOM access, so React Native and the web share one derivation.
 */

import { converter, type Oklch, parse } from 'culori/fn';
import { higherContrastInk, type Ink } from './color-contrast';

export const THEME_NAMES = ['blue', 'brown', 'green', 'grey', 'orange', 'purple', 'red', 'yellow'] as const;

export type ThemeName = (typeof THEME_NAMES)[number];

export const DEFAULT_THEME: ThemeName = 'blue';

/** Narrow an arbitrary string (e.g. the backend `company-theme`) to a known theme. */
export const toThemeName = (value: string | undefined | null): ThemeName => {
  return THEME_NAMES.includes(value as ThemeName) ? (value as ThemeName) : DEFAULT_THEME;
};

export const THEME_BRANDS: Record<ThemeName, string> = {
  blue: 'oklch(0.42 0.16 260)',
  brown: 'oklch(0.48 0.03 62)',
  green: 'oklch(0.48 0.15 145)',
  grey: 'oklch(0.48 0 0)',
  orange: 'oklch(0.7 0.16 57)',
  purple: 'oklch(0.48 0.2 297)',
  red: 'oklch(0.48 0.19 29)',
  yellow: 'oklch(0.85 0.17 90)',
};

/** Mirrors `max(l, 0.72)` in theme.css. Below it a brand is invisible on the dark canvas. */
export const DARK_LIGHTNESS_FLOOR = 0.72;

export interface ThemeAccent {
  /** `--primary`. */
  oklch: { light: string; dark: string };
  /** `--primary-foreground`. */
  foreground: { light: Ink; dark: Ink };
}

const toOklch = converter('oklch');

const round = (value: number): number => Number(value.toFixed(4));

const format = ({ l, c, h = 0 }: Oklch): string => `oklch(${round(l)} ${round(c)} ${round(h)})`;

/** Takes hex, `rgb()` or `oklch()`. */
export const deriveThemeAccent = (brand: string): ThemeAccent => {
  const parsed = parse(brand);
  if (!parsed) {
    throw new Error(`Unparseable colour: ${brand}`);
  }
  const light = toOklch(parsed);
  const dark: Oklch = { ...light, l: Math.max(light.l, DARK_LIGHTNESS_FLOOR) };

  const oklch = { light: format(light), dark: format(dark) };

  return {
    oklch,
    foreground: { light: higherContrastInk(oklch.light), dark: higherContrastInk(oklch.dark) },
  };
};

export const THEME_ACCENTS: Record<ThemeName, ThemeAccent> = Object.fromEntries(
  THEME_NAMES.map((name) => [name, deriveThemeAccent(THEME_BRANDS[name])]),
) as Record<ThemeName, ThemeAccent>;
